import { Injectable } from '@angular/core';
import { AuthService } from 'services/auth.service';
import { UserDb } from 'services/user-db.service';
import { UrlQueryService } from 'services/url-query-service';
import { NavController } from 'providers/nav-controller';
import { getMyApp } from 'app/app-interface';
import { getAllDeepLinks, getPageInterface } from 'app/app-page-helpers';
import { LoggingService } from 'services/logging.service';
import { NotificationService } from 'services/notification.service';
import { decidePushTapFailureHandling, PushTapFailureHandling } from 'services/push-tap-ready-gate';
import { describeError } from '@cf-shared/error-describe';
import { CoachManageNameSource, findClientNickname, pickCoachManageDisplayName } from 'services/coach-manage-display-name';
import { LinkTiming, parseCoachDeepLinkPage, PendingNotificationLinks, takeStashedCoachDeepLinkPage } from 'services/coach-manage-deep-link';
import { AuthRole } from '@cf-shared/couch-auth';

/**
 * Where the page a deep link asked for is kept while the app restarts.
 *
 * Entering a client's account restarts the app, so the target page cannot simply be navigated to
 * — it has to survive the restart, and session storage is what does that. Written by
 * `processCoachManageAsync` below and by `coach-client-detail.ts` when the coach taps
 * "Review Intake"; read once, and cleared, by `navigateToDeepLinkPageAsync`.
 */
export const COACH_DEEP_LINK_PAGE_KEY = 'coachDeepLinkPage';

/**
 * Coach manage-mode deep linking: a coach followed a link — from an email or a push notification
 * — that points at one of their clients.
 *
 * The whole flow is three steps with an app restart in the middle. `hasPendingCoachManage` asks
 * whether such a link is waiting; `processCoachManageAsync` enters the client's account, stashes
 * the page to open, and restarts the app; `navigateToDeepLinkPageAsync` runs after the restart
 * and opens that page.
 *
 * This is NOT the coach invitation claim, which is a different thing that used to live in the
 * same file: that one is a brand-new client opening the link their coach sent them, and it lives
 * in `coach-claim.service.ts`. The two share no state and no failure mode.
 */
@Injectable({ providedIn: 'root' })
export class CoachManageDeepLinkService {

  /**
   * The notification links waiting for the app to finish starting, and the rules for when each
   * may be acted on (#6433). The rules are in `PendingNotificationLinks`, which is unit-tested
   * on its own; this service only supplies the navigation.
   */
  private readonly pendingLinks = new PendingNotificationLinks();

  constructor(
    private auth: AuthService,
    private userDb: UserDb,
    private urlQuery: UrlQueryService,
    private navCtrl: NavController,
    private logging: LoggingService,
    notifications: NotificationService,
  ) {
    // act on remembered links once the signed-in app is ready (#6573). this used to run from the
    // paid-user background tasks, which a warm resume starts only after its server login, and
    // skips when that login fails, so the link waited on the network or was lost with no report.
    // the ready moment also waits out the navigation restore, which would replace the page opened
    void notifications.whenSignedInAppReadyAsync().then(() => this.actOnRememberedLinksAsync());
  }

  /** Once the app is ready: open the page or enter the account a remembered link named, if any */
  private async actOnRememberedLinksAsync(): Promise<void> {
    // if the person has no paid access, act on nothing; the same rule the background tasks had
    if (this.auth.getAuthRole() < AuthRole.Paid) return;
    try {
      await this.navigateToDeepLinkPageAsync();
    } catch (err) {
      // if a sign-out shut the user database down under the link (a warm resume whose background
      // login found the session ended), the person is going to the login page: record it quietly,
      // the same split a push tap makes (df-72eb80b3f2c5)
      if (decidePushTapFailureHandling(err) === PushTapFailureHandling.DropForSignOut) {
        this.logging.trackSilentInfo('NotificationLinkDroppedDuringSignOut', { error: describeError(err) });
        return;
      }
      this.logging.trackSilentProblem('NotificationLinkOpenFailed', { error: describeError(err) });
    }
  }

  /** Whether a coachManage deep link is waiting in the URL */
  hasPendingCoachManage(): boolean {
    return !!this.urlQuery.getUrlParam('coachManage');
  }

  /** Handle coachManage deep link: enter client-manage mode and reload.
   *  Args override URL params — used by push-notification taps that carry data directly. */
  async processCoachManageAsync(args?: { clientUid: string; page?: string }): Promise<void> {
    const clientUid = args?.clientUid ?? this.urlQuery.getUrlParam('coachManage');
    const page = args?.page ?? this.urlQuery.getUrlParam('page');
    if (!clientUid) return;

    // if this link is already being acted on, do nothing: a link that starts the phone app is
    // handed over twice, and a second pass would start a second reload on top of the first
    if (!this.pendingLinks.tryBeginEnteringClientAccount(this.navCtrl.reloadPending)) return;

    try {
      // if the deep link came from the URL (email path), clear params so they don't persist after reload.
      // on the push-tap path, args are provided and there are no URL params to clear.
      if (!args) {
        this.urlQuery.clearUrlSearchKeys(['coachManage', 'page']);
      }

      const clientDisplayName = await this.resolveManagedClientNameAsync(clientUid);

      // store page for post-reload navigation
      if (page) {
        sessionStorage.setItem(COACH_DEEP_LINK_PAGE_KEY, page);
      }

      // enter client-manage mode and reload
      this.userDb.setCoachManagingClient({ clientUid, clientDisplayName });
      await getMyApp().reloadAppAsync({ resetToRoot: true });
    } finally {
      this.pendingLinks.finishEnteringClientAccount();
    }
  }

  /**
   * Work out what the "Managing" banner should call this client.
   *
   * The name the coach chose for a client lives only in the coach's OWN database, and this
   * method can run while a DIFFERENT database is open — which is how a raw user id used to
   * reach the banner (#4902). The order the sources are tried in is
   * `pickCoachManageDisplayName`, which is unit-tested on its own; the reason each source
   * exists is in `design-docs/coach-mode.md` ("Naming the managed client").
   */
  private async resolveManagedClientNameAsync(clientUid: string): Promise<string> {
    const storedManagingClient = this.userDb.getCoachManagingClient();

    // Each read's error is kept whole and dealt with below, once the outcome is known. Which
    // report a failure deserves depends on whether a later source went on to supply the name,
    // and that is not known yet at the point the failure happens.
    let activeDbError: unknown;
    let ownDbError: unknown;

    let activeDbNickname: string | undefined;
    // Skipped when a manage session is ALREADY running, because then the open database belongs
    // to the client being managed and provably cannot hold the coach's roster — the own-database
    // read below is what answers this case, and it runs on exactly the same condition. Asking
    // anyway would only ever return the empty default, and `loadCoachRelAsCoach` now reports
    // that as a problem, so the one caller that knowingly looked in the wrong place would be
    // the only thing ever raising it.
    if (!storedManagingClient) {
      try {
        activeDbNickname = findClientNickname(this.userDb.loadCoachRelAsCoach(), clientUid);
      } catch (err) {
        activeDbError = err;
      }
    }

    let ownDbNickname: string | undefined;
    // Only worth doing when a manage session is already active. Without one the open database
    // IS the coach's own, so this would re-read what was just read.
    if (!activeDbNickname && storedManagingClient) {
      try {
        const ownRoster = await this.userDb.loadCoachRosterFromOwnDbAsync(this.auth.user?.uid || '');
        ownDbNickname = findClientNickname(ownRoster, clientUid);
      } catch (err) {
        ownDbError = err;
      }
    }

    const decision = pickCoachManageDisplayName({
      clientUid, activeDbNickname, ownDbNickname, storedManagingClient,
    });

    const failures = {
      wasAlreadyManaging: !!storedManagingClient,
      activeDbFailure: activeDbError === undefined ? undefined : describeError(activeDbError),
      ownDbFailure: ownDbError === undefined ? undefined : describeError(ownDbError),
    };

    if (decision.source === CoachManageNameSource.ClientUidFallback) {
      // The coach is about to see a screen that names their client by an unreadable id, and it
      // stays that way for the whole session because it is written into the stored manage
      // state. Nothing has repaired itself, so this is the problem tier.
      this.logging.trackSilentProblem('CoachManage: client nickname not found', failures);
    } else if (activeDbError !== undefined || ownDbError !== undefined) {
      // A read failed and a later source supplied the name anyway. The repair has already
      // happened by the time this line runs, which is what the info tier is for — but the
      // failed read still has to reach a future debugger rather than vanish.
      this.logging.trackSilentInfo('CoachManage: client nickname read failed but recovered', {
        ...failures, recoveredFrom: decision.source,
      });
    }

    return decision.displayName;
  }

  /**
   * Take a CLIENT's notification link out of the address the app was loaded at, if there is one
   * (#6433).
   *
   * Such a link is `/?page=weekly-checkup:id~…` with no `coachManage`: the button in the
   * "check-up reviewed" email, which nothing used to read, so the client landed on the home page.
   * The page is only remembered here. It is opened by `navigateToDeepLinkPageAsync`, once the app
   * has finished starting, on top of the home page.
   *
   * A page the allow-list refuses is left in the address and never opened.
   */
  adoptClientPageFromUrl(): void {
    // a coach link is handled by processCoachManageAsync, which reads `page` itself
    if (this.hasPendingCoachManage()) return;

    // if the allow-list refuses the page, leave the address alone
    if (this.pendingLinks.rememberClientPage(this.urlQuery.getUrlParam('page')) === LinkTiming.Refused) return;

    // the link has been taken, so it must not be read again on a refresh
    this.urlQuery.clearUrlSearchKeys(['page']);
  }

  /**
   * Open a page of the signed-in person's own account that a notification link named, when the
   * operating system handed the link to the phone app (#6433).
   *
   * If the app has already finished starting, the page opens now. If the link is what started
   * the app, it is only remembered, and the start-up opens it at the same point it opens a
   * coach's stashed page.
   */
  async openClientPageAsync(page: string): Promise<void> {
    // if the app has finished starting, open the page now; otherwise the start-up opens it
    if (this.pendingLinks.rememberClientPage(page) === LinkTiming.Now) {
      await this.navigateToDeepLinkPageAsync();
    }
  }

  /**
   * Enter a client's account because the operating system handed the phone app a coach's
   * notification link (#6433).
   *
   * If the app has already finished starting, this enters the account now. If the link is what
   * started the app, it is only remembered, and `navigateToDeepLinkPageAsync` enters the account
   * once the app has started. Entering straight away on a cold start does work, but the coach's
   * own database is not open yet, so the client's nickname cannot be read and the "Managing"
   * banner shows the client's raw account id for the whole session. Seen on a device on
   * 2026-10-12.
   */
  async enterClientAccountFromLinkAsync(clientUid: string, page?: string): Promise<void> {
    // if the app has finished starting, enter the account now; otherwise the start-up does
    if (this.pendingLinks.rememberCoachLink({ clientUid, page }) === LinkTiming.Now) {
      await this.processCoachManageAsync({ clientUid, page });
    }
  }

  /** Once the app has started (and after a coach's reload), open the page a deep link named, if any */
  async navigateToDeepLinkPageAsync(): Promise<void> {
    this.pendingLinks.markAppStarted();

    // if a coach's link started the app, enter that client's account now that the coach's own
    // database is open. this reloads the app, and the reloaded app opens the page the link named
    const coachLink = this.pendingLinks.takeCoachLink();
    if (coachLink) {
      await this.processCoachManageAsync(coachLink);
      return;
    }

    // a client's own link comes first; otherwise the page a coach's link stashed before its
    // reload. while a reload is pending this page is about to be replaced, so the stash is left
    // for the reloaded app — the reason is on takeStashedCoachDeepLinkPage
    const clientPage = this.pendingLinks.takeClientPage({
      reloadPending: this.navCtrl.reloadPending,
      hasPaidAccess: this.auth.getAuthRole() >= AuthRole.Paid,
    });
    const page = clientPage
      ?? takeStashedCoachDeepLinkPage(sessionStorage, COACH_DEEP_LINK_PAGE_KEY, this.navCtrl.reloadPending);
    if (!page) return;

    // decode the stashed string into a page segment and its parameters — the format, and the
    // intake-review change-mode rule, live in coach-manage-deep-link.ts with their own tests
    const { segment, params } = parseCoachDeepLinkPage(page);

    // find the page name from the segment
    const deepLink = getAllDeepLinks().find(link => link.segment?.split('/')[0] === segment);
    if (!deepLink) {
      console.log(`[COACH] deep link: unknown page segment "${segment}"`);
      return;
    }

    await this.navCtrl.pushAsync(getPageInterface(deepLink.name), params);
  }
}
